'use client';

import { ScrollArea as ScrollAreaPrimitive } from '@base-ui-components/react';
import { styled, SxProps, type Theme } from '@mui/material';
import { forwardRef, type ComponentPropsWithoutRef, type ComponentPropsWithRef, type ComponentType } from 'react';
import { themeVars } from '../../theme/themeVars';

export interface RootOwnProps {
  orientation?: 'vertical' | 'horizontal' | 'both';
  sx?: SxProps<Theme>;
}

export interface ViewportOwnProps {
  sx?: SxProps<Theme>;
}

export interface ContentOwnProps {
  sx?: SxProps<Theme>;
}

type DivPrimitiveProps = ComponentPropsWithoutRef<'div'> & {
  sx?: SxProps<Theme>;
};

type RootProps = DivPrimitiveProps & RootOwnProps;
type StyledDivPrimitiveProps = ComponentPropsWithRef<'div'> & {
  sx?: SxProps<Theme>;
};
type ScrollbarPrimitiveProps = StyledDivPrimitiveProps & {
  orientation: 'vertical' | 'horizontal';
};

const StyledRoot = styled(ScrollAreaPrimitive.Root as ComponentType<StyledDivPrimitiveProps>)();

const Viewport = styled(ScrollAreaPrimitive.Viewport as ComponentType<StyledDivPrimitiveProps>)({
  height: '100%',
  overscrollBehavior: 'contain',
});

const Content = styled(ScrollAreaPrimitive.Content as ComponentType<StyledDivPrimitiveProps>)();

const Scrollbar = styled(ScrollAreaPrimitive.Scrollbar as ComponentType<ScrollbarPrimitiveProps>)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  borderRadius: theme.shape.borderRadius,
  margin: 1,
  opacity: 0,
  transition: 'opacity 150ms 500ms',
  '&[data-hovering], &[data-scrolling]': {
    opacity: 1,
    transitionDuration: '150ms',
    transitionDelay: '0ms',
  },
  '&[data-orientation="vertical"]': {
    width: 6,
    '&::before': {
      content: '""',
      position: 'absolute',
      width: '1.25rem',
      height: '100%',
    },
  },
  '&[data-orientation="horizontal"]': {
    height: 6,
    flexDirection: 'column',
    '&::before': {
      content: '""',
      position: 'absolute',
      width: '100%',
      height: '1.25rem',
    },
  },
}));

const Thumb = styled(ScrollAreaPrimitive.Thumb as ComponentType<StyledDivPrimitiveProps>)(({ theme }) => ({
  borderRadius: 'inherit',
  backgroundColor: themeVars(theme).palette.scrollbar.thumb,
  '&[data-orientation="vertical"]': {
    width: '100%',
  },
  '&[data-orientation="horizontal"]': {
    height: '100%',
  },
}));

const Corner = styled(ScrollAreaPrimitive.Corner as ComponentType<StyledDivPrimitiveProps>)();

// eslint-disable-next-line react-refresh/only-export-components
const Root = forwardRef<HTMLDivElement, RootProps>(function ScrollAreaRoot(props, ref) {
  const { orientation = 'vertical', children, ...rest } = props;
  return (
    <StyledRoot data-testid="mds-scroll-root" {...rest} ref={ref}>
      {children}

      {(orientation === 'vertical' || orientation === 'both') && (
        <Scrollbar orientation="vertical">
          <Thumb />
        </Scrollbar>
      )}

      {(orientation === 'horizontal' || orientation === 'both') && (
        <Scrollbar orientation="horizontal">
          <Thumb />
        </Scrollbar>
      )}

      {orientation === 'both' && <Corner />}
    </StyledRoot>
  );
});

/**
 * Components for creating scrollable content areas
 */
export const ScrollArea = {
  /**
   * Scroll area root.
   *
   * Contains the viewport and scrollbars. Scroll area width and height is usually applied
   * to this element.
   */
  Root,
  /**
   * Scrollable element (overflow: scroll)
   */
  Viewport,
  /**
   * Content to scroll.
   *
   * Content padding is usually applied to this element.
   */
  Content,
};
